import * as THREE from 'three';
import { VoxelGrid } from '../core/voxel-grid.js';

// ---------------------------------------------------------------------------
// The Sint-Vincentiuskerk, Volendam.
//
// Brick, with a square tower on the front carrying a clock and an arched
// belfry, and a slate spire above it that is the tallest thing on the island.
// Behind the tower a gabled nave with round windows down its flanks, and a
// rounded apse at the far end.
//
// It gives Volendam a second thing to know it by from the water: masts at the
// harbour, a spire behind them.
// ---------------------------------------------------------------------------

export function registerChurchBlocks(palette) {
    return {
        brick: palette.add('churchBrick', 0x8a6f62, { jitter: 0.06 }),
        brickDark: palette.add('churchBrickDark', 0x6f594f, { jitter: 0.06 }),
        slate: palette.add('churchSlate', 0x50565f, { jitter: 0.05 }),
        slateDark: palette.add('churchSlateDark', 0x3b4048, { jitter: 0.05 }),
        clock: palette.add('churchClock', 0xd8b447, { emissive: 0.25, jitter: 0.03 }),
        glass: palette.add('churchGlass', 0xffd79a, { emissive: 0.9, jitter: 0.03 }),
        stone: palette.add('churchStone', 0xcfc6b4, { jitter: 0.04 }),
    };
}

const W = 13;    // nave width
const L = 24;    // nave length, tower included
const TOWER = 7; // tower footprint

export function createChurch(palette, material, P, { voxelSize }) {
    const H = 46;
    const g = new VoxelGrid(L, H, W);
    const cz = (W - 1) / 2;

    // --- nave ------------------------------------------------------------
    const navX0 = TOWER;
    const walls = 11;
    g.box(navX0, 0, 1, L - 3, walls, W - 2, P.brick);

    // A course of darker brick at the foot, as the real one has.
    g.box(navX0, 0, 1, L - 3, 1, W - 2, P.brickDark);

    // Round windows down both flanks, and taller ones between them.
    for (let x = navX0 + 3; x < L - 4; x += 4) {
        g.set(x, walls - 3, 1, P.glass);
        g.set(x, walls - 3, W - 2, P.glass);
        g.box(x, 4, 1, x, 6, 1, P.glass);
        g.box(x, 4, W - 2, x, 6, W - 2, P.glass);
    }

    // Gabled roof: steps in from both eaves to a ridge along the nave.
    for (let k = 0; k <= Math.ceil((W - 2) / 2); k++) {
        const y = walls + 1 + k;
        const z0 = 1 + k;
        const z1 = W - 2 - k;
        if (z0 > z1) break;
        g.box(navX0 - 1, y, z0, L - 2, y, z1, k === 0 ? P.slateDark : P.slate);
    }

    // --- apse: the rounded end ------------------------------------------
    for (let k = 0; k < 3; k++) {
        g.box(L - 3 + k, 0, 2 + k, L - 3 + k, walls - k, W - 3 - k, P.brick);
    }

    // --- tower ------------------------------------------------------------
    const t0 = 0;
    const t1 = TOWER - 1;
    const tz0 = Math.round(cz - (TOWER - 1) / 2);
    const tz1 = tz0 + TOWER - 1;
    const TOP = 30;
    g.box(t0, 0, tz0, t1, TOP, tz1, P.brick);
    g.box(t0, 0, tz0, t1, 1, tz1, P.brickDark);

    // Door, and the arched window above it.
    g.box(1, 1, cz - 1, 1, 3, cz + 1, P.brickDark);
    g.box(0, 5, cz - 1, 0, 8, cz + 1, P.glass);

    // The clock, high on the seaward face.
    g.box(0, TOP - 9, cz - 1, 0, TOP - 7, cz + 1, P.clock);
    g.set(0, TOP - 8, cz, P.brickDark);

    // Belfry: tall arched openings on every face.
    for (let y = TOP - 5; y <= TOP - 2; y++) {
        g.box(t0, y, cz - 1, t0, y, cz + 1, P.slateDark);
        g.box(t1, y, cz - 1, t1, y, cz + 1, P.slateDark);
        g.box(t0 + 1, y, tz0, t1 - 1, y, tz0, P.slateDark);
        g.box(t0 + 1, y, tz1, t1 - 1, y, tz1, P.slateDark);
    }
    // A stone cornice under the spire.
    g.box(t0 - 1, TOP + 1, tz0 - 1, t1 + 1, TOP + 1, tz1 + 1, P.stone);

    // --- spire ------------------------------------------------------------
    // Steps in a ring at a time to a point, which is what makes it read as
    // slate rather than as a cone. Centred on the tower, which is the whole
    // trick: the cross has to sit on the middle of it, not on a corner.
    const towerX = Math.round((t0 + t1) / 2);
    let s0 = t0;
    let s1 = t1;
    let sz0 = tz0;
    let sz1 = tz1;
    let y = TOP + 2;
    while (s0 <= s1 && sz0 <= sz1 && y < H - 5) {
        g.box(s0, y, sz0, s1, y, sz1, P.slate);
        y += 1;
        // Shrink every other course, so the pitch is steep but stepped.
        if ((y - TOP) % 2 === 0) {
            s0 += 1; s1 -= 1; sz0 += 1; sz1 -= 1;
        }
    }

    // The finial and its cross, standing on the point rather than above it.
    g.box(towerX, y, cz, towerX, y + 2, cz, P.slateDark);
    g.set(towerX, y + 1, cz - 1, P.slateDark);
    g.set(towerX, y + 1, cz + 1, P.slateDark);

    const mesh = new THREE.Mesh(
        g.build(palette, {
            voxelSize,
            // Tower foot at the group origin, nave running along +X.
            origin: new THREE.Vector3(0, 0, -cz * voxelSize),
        }),
        material,
    );
    mesh.name = 'church';
    return { mesh, height: H * voxelSize, length: L * voxelSize };
}
